/**
 * CompanyMarketingEditor v3 — iter-198 (Feb 2026)
 *
 * User feedback addressed:
 *  1. Tags removed from UI + never exported.
 *  2. Internal-only notes field added (saved in `data.internal_notes`, NEVER rendered).
 *  3. Title removed from image exports (still used for filename & inside PDF hero only).
 *  4. Plate code = dropdown driven by emirate+category (max 5-digit number only).
 *  5. Download button opens a dropdown of proper Instagram sizes:
 *       Phone / Plate  → Story 1080x1920 · Post 1080x1080
 *       Car / Real Est → Story 1080x1920 · Post 1080x1080 · PDF A4
 *  6. Card/PDF redesign — matches Manual Task PDF exactly:
 *       - off-white paper (#f8fafc) instead of dark
 *       - CloudGM blue↔orange gradient accents
 *       - LOGO used as-is (full "CLOUD GM" wordmark, NEVER cropped or supplemented
 *         with any AI-invented text like "MARKETING" or "Cloud GM Business")
 *  7. Description is rendered when provided (both card + PDF).
 *
 * Reads fields DYNAMICALLY from the Schema Registry (/api/admin/listing-schemas).
 */
import { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import * as htmlToImage from 'html-to-image';
import {
  ChevronLeft, Loader2, Save, Upload, X, Phone, Car, Building2, Hash,
  Trash2, Pencil, Sparkles, Camera,
  FileText, ImageDown, Languages, Download, ChevronDown,
} from 'lucide-react';

import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { getAuthHeaders } from '../../utils/authHeaders';
import UAEPlate, { hasPlateDesign } from '../../components/plates/UAEPlate';
import { DirhamPrice } from '../../components/DirhamPrice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────
// Brand — matches Manual Task PDF (printAssignedTask.js) exactly
// ─────────────────────────────────────────────────────────────
const LOGO_URL = '/pdf-logo-cloudgm.png'; // full CLOUD GM wordmark. Never cropped.
const BRAND = {
  blue:   '#3B9EDC',
  blue700:'#0369a1',
  orange: '#F5A55A',
  orange700:'#d97706',
  ink:    '#0f172a',
  ink600: '#334155',
  ink400: '#64748b',
  ink200: '#e2e8f0',
  paper:  '#ffffff',
  tint:   '#f8fafc',   // ← off-white paper the user liked
  gold:   '#d4af37',   // price accent only
  gradient: 'linear-gradient(90deg, #3B9EDC 0%, #F5A55A 100%)',
};
const CONTACT = {
  sites:  ['cloudgm.com', 'cloudgm.business'],
  phone:  '+971 50 151 1917',
  emails: ['support@cloudgm.business', 'support@cloudgm.com'],
};

// short type (from marketing hub) → schema category (from Schema Registry)
const TYPE_TO_CATEGORY = {
  phone: 'phone_numbers',
  plate: 'plates',
  car:   'cars',
  real_estate: 'real_estate',
};

const TYPE_META = {
  phone:       { icon: Phone,     color: 'blue',    ar: 'رقم هاتف',    en: 'Phone Number' },
  plate:       { icon: Hash,      color: 'amber',   ar: 'لوحة سيارة',  en: 'Car Plate' },
  car:         { icon: Car,       color: 'emerald', ar: 'سيارة',       en: 'Car' },
  real_estate: { icon: Building2, color: 'purple',  ar: 'عقار',        en: 'Real Estate' },
};

// ─────────────────────────────────────────────────────────────
// Plate code catalog — official code options per emirate/category
// ─────────────────────────────────────────────────────────────
// Values are the *displayed* code (as printed on real UAE plates).
const PLATE_CODES = {
  abu_dhabi: {
    private:    ['1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','17','18','19','20','21','22','23','24','25','50'],
    motorcycle: ['M'],
    classic:    ['1','2','3','4','5'],
  },
  dubai: {
    private:    ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z','AA'],
    motorcycle: ['M'],
    classic:    [],   // classic Dubai plates have no code — number only
  },
  sharjah: {
    private:    ['1','2','3','4'],
    motorcycle: ['1'],
    classic:    [],
  },
  ajman: {
    private:    ['A','B','C','D','E','F','G','H'],
    motorcycle: ['A'],
    classic:    [],
  },
  umm_al_quwain: {
    private:    ['A','B','C','D','E','F','G','H','I'],
    motorcycle: ['A'],
    classic:    [],
  },
  ras_al_khaimah: {
    // iter-217c-v11 (Feb 2026) — completed RAK private codes (A-Z) per user
    // request. RAK currently issues single-letter codes A through Z.
    private: [
      'A','B','C','D','E','F','G','H','I','J','K','L','M',
      'N','O','P','Q','R','S','T','U','V','W','X','Y','Z',
    ],
    motorcycle: ['A'],
    classic:    [],   // RAK classic uses number only
  },
  fujairah: {
    private:    ['A','B','C','D','E','F','G','H','I','J','K','L','M'],
    motorcycle: ['A'],
    classic:    [],
  },
};

function optionsForCode(emirate, category) {
  return PLATE_CODES[emirate]?.[category] || [];
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function optionLabel(field, value, isAr) {
  if (!field?.options || value == null) return value;
  const opt = field.options.find(o => String(o.value) === String(value));
  if (!opt) return value;
  return isAr ? (opt.label_ar || opt.label_en || opt.value) : (opt.label_en || opt.label_ar || opt.value);
}

// Image export sizes (Instagram)
const EXPORT_SIZES = {
  story: { w: 1080, h: 1920, label_ar: 'ستوري (Story)',  label_en: 'Instagram Story',   ratio: 'aspect-[9/16]' },
  post:  { w: 1080, h: 1080, label_ar: 'بوست (Post)',    label_en: 'Instagram Post',    ratio: 'aspect-square' },
};

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function CompanyMarketingEditor({
  language, mode: initialMode, type, item, onClose,
  autoExport,           // iter-208 — 'story' | 'post' | 'pdf' (auto-download then close)
}) {
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  // iter-208 — local mode state so "عرض" can toggle to "تعديل" inside the editor.
  const [mode, setMode] = useState(initialMode || 'create');
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const meta = TYPE_META[type];
  const Icon = meta?.icon;
  const category = TYPE_TO_CATEGORY[type];

  const [exportLang, setExportLang] = useState(isAr ? 'ar' : 'en');
  const exportIsAr = exportLang === 'ar';

  const [schema, setSchema] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(true);

  const [title, setTitle] = useState(item?.title || '');
  const [data, setData] = useState(item?.data || {});
  const [images, setImages] = useState(item?.images || []);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savedId, setSavedId] = useState(item?.id || null);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  // iter-215 — AI auto-fill from image (car / plate only).
  const [aiExtracting, setAiExtracting] = useState(false);
  const aiFileInputRef = useRef(null);
  const supportsAI = type === 'car' || type === 'plate';

  const setD = (k, v) => setData((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/admin/listing-schemas/${category}`,
          { headers: getAuthHeaders() },
        );
        if (mounted) setSchema(r.data);
      } catch {
        toast.error(t('فشل تحميل الحقول', 'Failed to load fields'));
      } finally {
        if (mounted) setLoadingSchema(false);
      }
    })();
    return () => { mounted = false; };
  }, [category]);

  const fields = useMemo(() => {
    if (!schema?.fields) return [];
    return [...schema.fields].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [schema]);

  const handleImages = async (files) => {
    const list = Array.from(files || []);
    const encoded = await Promise.all(list.map((f) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    })));
    setImages((imgs) => [...imgs, ...encoded]);
  };

  // iter-215 — auto-fill car/plate fields from a photo via Gemini OCR.
  const handleAIExtract = async (file) => {
    if (!file || !supportsAI) return;
    // 5MB browser-side guard (matches backend cap).
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('الصورة أكبر من 5MB — من فضلك اضغطها ثم أعد المحاولة', 'Image is over 5MB — please compress it first'));
      return;
    }
    setAiExtracting(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await axios.post(
        `${API_URL}/api/admin/company-marketing/ai/extract-from-image`,
        { image_base64: dataUrl, mime: file.type || 'image/jpeg', listing_type: type },
        { headers: getAuthHeaders() },
      );
      const extracted = res.data?.data || {};
      const keys = Object.keys(extracted);
      if (keys.length === 0) {
        toast.warning(t('لم يتم استخراج بيانات — جرب صورة أوضح', 'Nothing was extracted — try a clearer photo'));
        return;
      }
      // Merge — do not overwrite fields the admin already typed.
      setData((prev) => {
        const merged = { ...prev };
        for (const k of keys) {
          if (merged[k] === undefined || merged[k] === '' || merged[k] === null) {
            merged[k] = extracted[k];
          }
        }
        return merged;
      });
      // Auto-fill title if it's still empty (helpful default).
      if (!title.trim()) {
        if (type === 'car' && (extracted.make || extracted.model)) {
          setTitle(`${extracted.make || ''} ${extracted.model || ''} ${extracted.year || ''}`.trim());
        } else if (type === 'plate' && extracted.number) {
          setTitle(`${extracted.code || ''} ${extracted.number}`.trim());
        }
      }
      toast.success(t(`✓ تم استخراج ${keys.length} حقل`, `✓ Extracted ${keys.length} field(s)`));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشل الاستخراج', 'Extraction failed'));
    } finally {
      setAiExtracting(false);
      if (aiFileInputRef.current) aiFileInputRef.current.value = '';
    }
  };

  const save = async () => {
    if (!title.trim()) { toast.error(t('العنوان مطلوب', 'Title is required')); return; }
    setSaving(true);
    try {
      const body = {
        type,
        title: title.trim(),
        data,
        images,
        tags: [], // iter-198 — tags removed from UI. Always empty on new saves.
      };
      if (isEdit && item?.id) {
        await axios.patch(`${API_URL}/api/admin/company-marketing/${item.id}`, body, { headers: getAuthHeaders() });
        setSavedId(item.id);
      } else {
        const res = await axios.post(`${API_URL}/api/admin/company-marketing`, body, { headers: getAuthHeaders() });
        setSavedId(res.data?.id || res.data?.data?.id || 'saved');
      }
      toast.success(t('✓ تم الحفظ — يمكنك الآن التحميل', '✓ Saved — you can now download'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  // ─── Hidden export targets (re-renders on-demand for each size) ───
  const cardRef = useRef(null);
  const brochurePagesRef = useRef([]);  // iter-204 — array of refs, one per PDF page
  const [exportSize, setExportSize] = useState('post'); // 'story' | 'post'

  const downloadImageAt = async (size /* 'story' | 'post' */) => {
    setExportSize(size);
    setDownloadMenuOpen(false);
    // Wait for React to paint the new size
    await new Promise((r) => setTimeout(r, 60));
    if (!cardRef.current) { toast.error(t('حصل خطأ', 'Something went wrong')); return; }
    setGenerating(true);
    try {
      const dataUrl = await htmlToImage.toPng(cardRef.current, {
        pixelRatio: 2, cacheBust: true, skipFonts: false, backgroundColor: BRAND.tint,
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      const cleanTitle = (title || type).replace(/\s+/g, '_');
      a.download = `${cleanTitle}_${size}_${exportLang}.png`;
      a.click();
      toast.success(t('✓ تم التحميل', '✓ Downloaded'));
    } catch (err) {
      toast.error(t('فشل التحميل', 'Download failed') + ': ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = async () => {
    setDownloadMenuOpen(false);
    const pageEls = brochurePagesRef.current.filter(Boolean);
    if (pageEls.length === 0) { toast.error(t('حصل خطأ', 'Something went wrong')); return; }
    setGenerating(true);
    try {
      // iter-204: render EACH A4 page separately → each becomes exactly one
      // PDF page with no mid-content slicing. Small fields ride 2-columns.
      const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'p' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      for (let i = 0; i < pageEls.length; i += 1) {
        const el = pageEls[i];
        const png = await htmlToImage.toPng(el, {
          pixelRatio: 2, cacheBust: true, skipFonts: false, backgroundColor: BRAND.tint,
        });
        if (i > 0) doc.addPage();
        // Fit page image to full A4 without stretching (contain) — every page is
        // rendered at exactly A4 aspect so this simply fills the page.
        doc.addImage(png, 'PNG', 0, 0, pageW, pageH);
      }
      doc.save(`${(title || type).replace(/\s+/g, '_')}_${exportLang}.pdf`);
      toast.success(t('✓ تم تحميل الـ PDF', '✓ PDF downloaded'));
    } catch (err) {
      toast.error(t('فشل إنشاء الـ PDF', 'PDF generation failed') + ': ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const isImageCard = type === 'phone' || type === 'plate';
  const hasPDF = type === 'car' || type === 'real_estate';
  const canDownload = !generating && !!title && !!savedId;

  // iter-208 — autoExport prop: when set, silently trigger the requested
  // download once the hidden targets have rendered, then close. Used by the
  // "Print" dropdown on the card list.
  useEffect(() => {
    if (!autoExport || !savedId || loadingSchema) return;
    // Give the hidden refs a paint tick.
    const timer = setTimeout(async () => {
      try {
        if (autoExport === 'story')      await downloadImageAt('story');
        else if (autoExport === 'post')  await downloadImageAt('post');
        else if (autoExport === 'pdf')   await downloadPDF();
      } finally {
        onClose(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [autoExport, savedId, loadingSchema]);

  // iter-204 — compute brochure pages ahead of PDF generation.
  // Content is split so no logical block (spec row, description, gallery)
  // gets cut across pages. Small fields ride 2-columns to save space.
  const brochurePages = useMemo(() => {
    if (!hasPDF) return [];
    return computeBrochurePages({ data, fields, images, exportIsAr });
  }, [hasPDF, data, fields, images, exportIsAr]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="company-marketing-editor">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <button
          onClick={() => onClose(false)}
          className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/70"
          data-testid="company-marketing-editor-back"
        >
          <ChevronLeft className={`h-5 w-5 ${isAr ? 'rotate-180' : ''}`} />
        </button>
        <div className={`p-2 rounded-xl bg-${meta.color}-500/10 border border-${meta.color}-500/20`}>
          {Icon && <Icon className={`w-5 h-5 text-${meta.color}-400`} />}
        </div>
        <h2 className="text-xl font-bold text-white flex-1">
          {isView ? t('عرض', 'View') : isEdit ? t('تعديل', 'Edit') : t('إضافة', 'Add')} — {isAr ? meta.ar : meta.en}
        </h2>

        {/* iter-208 — mode-aware header actions */}
        {isView ? (
          <>
            <Button
              onClick={() => setMode('edit')}
              className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold"
              data-testid="company-marketing-editor-edit"
            >
              <Pencil className="w-4 h-4 me-1" />
              {t('تعديل', 'Edit')}
            </Button>
            <Button
              onClick={async () => {
                if (!window.confirm(t('حذف نهائي؟', 'Delete permanently?'))) return;
                try {
                  await axios.delete(
                    `${API_URL}/api/admin/company-marketing/${savedId}`,
                    { headers: getAuthHeaders() },
                  );
                  toast.success(t('✓ تم الحذف', '✓ Deleted'));
                  onClose(true);
                } catch (err) {
                  toast.error(err.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
                }
              }}
              variant="outline"
              className="border-red-500/30 text-red-300 hover:bg-red-500/10"
              data-testid="company-marketing-editor-delete"
            >
              <Trash2 className="w-4 h-4 me-1" />
              {t('حذف', 'Delete')}
            </Button>
          </>
        ) : (
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold"
            data-testid="company-marketing-editor-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin me-1" /> : <Save className="w-4 h-4 me-1" />}
            {t('حفظ', 'Save')}
          </Button>
        )}
      </div>

      {/* Two-column layout */}
      {/* iter-208 — In view mode: wrap form column in a disabled fieldset so
          all inputs become readOnly with pointer-events lock (no visual noise
          on downloads panel which stays interactive). */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Form column */}
        <fieldset
          disabled={isView}
          className={`lg:col-span-3 space-y-4 ${isView ? 'opacity-95 pointer-events-none' : ''}`}
          data-testid={isView ? 'company-marketing-editor-fieldset-view' : 'company-marketing-editor-fieldset-edit'}
        >
          {/* Common fields */}
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
            <label className="block text-white/70 text-sm mb-1">{t('العنوان', 'Title')} *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-[#050505] border-white/10 text-white mb-1"
              data-testid="company-marketing-title-input"
            />
            <p className="text-[11px] text-white/40 mb-4">
              {t('العنوان يُستخدم داخلياً وفي اسم الملف — لا يظهر في الصورة النهائية.',
                 'Used internally and for the filename — not shown on the exported image.')}
            </p>

            <label className="block text-white/70 text-sm mb-1">
              {t('ملاحظات داخلية', 'Internal notes')}
              <span className="text-white/30 text-[10px] ms-2">
                ({t('لن تظهر في الصورة/PDF', 'not shown on card/PDF')})
              </span>
            </label>
            <textarea
              value={data.internal_notes || ''}
              onChange={(e) => setD('internal_notes', e.target.value)}
              rows={2}
              placeholder={t('ملاحظات للأدمن فقط…', 'Admin-only notes…')}
              className="w-full rounded-md bg-[#050505] border border-white/10 text-white p-2.5 text-sm"
              data-testid="company-marketing-internal-notes"
            />
          </div>

          {/* Dynamic schema fields */}
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
            {/* iter-215 — AI auto-fill (car & plate only) */}
            {supportsAI && !isView && (
              <div className="mb-4 p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/25">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-purple-300" />
                      <h4 className="text-white text-sm font-bold">
                        {t('استخراج تلقائي بالذكاء الاصطناعي', 'AI Auto-fill from Photo')}
                      </h4>
                    </div>
                    <p className="text-white/60 text-xs leading-relaxed">
                      {type === 'car'
                        ? t('ارفع صورة السيارة وسنملأ الماركة والموديل واللون والسنة تلقائياً — أي حقل ما نعرفه، تعبّيه أنت.',
                            'Upload a car photo and we\'ll auto-fill make, model, color, year — anything we can\'t detect, you fill.')
                        : t('ارفع صورة اللوحة وسنقرأ الإمارة والفئة والكود والرقم تلقائياً.',
                            'Upload a plate photo and we\'ll read the emirate, category, code and number automatically.')}
                    </p>
                  </div>
                  <input
                    ref={aiFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleAIExtract(e.target.files[0])}
                  />
                  <Button
                    onClick={() => aiFileInputRef.current?.click()}
                    disabled={aiExtracting}
                    className="bg-purple-500 hover:bg-purple-500/90 text-white font-bold shrink-0"
                    data-testid="ai-extract-btn"
                  >
                    {aiExtracting
                      ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('يقرأ الصورة…', 'Reading…')}</>
                      : <><Camera className="w-4 h-4 me-2" />{t('رفع صورة', 'Upload Photo')}</>}
                  </Button>
                </div>
              </div>
            )}
            {loadingSchema ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 text-white/40 animate-spin" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-sm">
                    {t('الحقول', 'Fields')}
                    <span className="text-white/40 text-xs ms-2">
                      v{schema?.version} · {fields.length} {t('حقل', 'fields')}
                    </span>
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {fields.map((field) => (
                    <DynamicField
                      key={field.key}
                      field={field}
                      value={data[field.key]}
                      onChange={(v) => setD(field.key, v)}
                      isAr={isAr}
                      allData={data}
                      type={type}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Images (for car/real_estate) */}
          {!isImageCard && (
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
              <ImageUploader
                images={images}
                setImages={setImages}
                handleImages={handleImages}
                t={t}
              />
            </div>
          )}
        </fieldset>

        {/* Right column — DOWNLOAD (post-save) */}
        <aside className="lg:col-span-2 space-y-4">
          <div className="sticky top-6">
            <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/10 rounded-2xl p-6">
              <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                {isImageCard
                  ? <ImageDown className="w-8 h-8 text-[#d4af37]" />
                  : <FileText className="w-8 h-8 text-[#d4af37]" />}
              </div>

              <h3 className="text-white font-bold text-center text-lg mb-1">
                {savedId
                  ? t('جاهز للتحميل', 'Ready to Download')
                  : t('احفظ أولاً ثم حمّل', 'Save first, then download')}
              </h3>
              <p className="text-white/50 text-xs text-center mb-5">
                {savedId
                  ? t('اختر المقاس من القائمة', 'Pick a size from the menu')
                  : t('بعد الحفظ ستظهر خيارات التحميل', 'Download options appear after Save')}
              </p>

              {/* Export language switch */}
              <div className="flex items-center justify-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10 mb-4">
                <Languages className="w-4 h-4 text-white/40 mx-1" />
                <button
                  onClick={() => setExportLang('ar')}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition ${exportLang === 'ar' ? 'bg-[#d4af37] text-black' : 'text-white/60 hover:text-white'}`}
                  data-testid="export-lang-ar"
                >
                  {t('عربي', 'Arabic')}
                </button>
                <button
                  onClick={() => setExportLang('en')}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition ${exportLang === 'en' ? 'bg-[#d4af37] text-black' : 'text-white/60 hover:text-white'}`}
                  data-testid="export-lang-en"
                >
                  {t('إنجليزي', 'English')}
                </button>
              </div>

              {/* Download dropdown — appears only after Save */}
              <div className="relative">
                <Button
                  onClick={() => canDownload && setDownloadMenuOpen((v) => !v)}
                  disabled={!canDownload}
                  className="w-full bg-emerald-500 hover:bg-emerald-500/90 text-white font-bold h-12 text-sm disabled:opacity-40"
                  data-testid="download-menu-btn"
                >
                  {generating
                    ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('جاري التوليد…', 'Generating…')}</>
                    : <><Download className="w-4 h-4 me-2" />{t('تحميل', 'Download')}<ChevronDown className="w-4 h-4 ms-2" /></>}
                </Button>

                {downloadMenuOpen && canDownload && (
                  <div
                    className="absolute z-20 mt-2 left-0 right-0 bg-[#0a0a0a] border border-white/10 rounded-xl shadow-2xl overflow-hidden"
                    data-testid="download-menu"
                  >
                    <button
                      onClick={() => downloadImageAt('story')}
                      className="w-full flex items-center justify-between px-4 py-3 text-sm text-white hover:bg-white/5 transition"
                      data-testid="download-option-story"
                    >
                      <span className="font-semibold">📱 {t('ستوري (Instagram)', 'Instagram Story')}</span>
                      <span className="text-[10px] text-white/50 font-mono">1080 × 1920</span>
                    </button>
                    <button
                      onClick={() => downloadImageAt('post')}
                      className="w-full flex items-center justify-between px-4 py-3 text-sm text-white hover:bg-white/5 transition border-t border-white/5"
                      data-testid="download-option-post"
                    >
                      <span className="font-semibold">🖼️ {t('بوست (Instagram)', 'Instagram Post')}</span>
                      <span className="text-[10px] text-white/50 font-mono">1080 × 1080</span>
                    </button>
                    {hasPDF && (
                      <button
                        onClick={downloadPDF}
                        className="w-full flex items-center justify-between px-4 py-3 text-sm text-white hover:bg-white/5 transition border-t border-white/5"
                        data-testid="download-option-pdf"
                      >
                        <span className="font-semibold">📄 {t('PDF (A4)', 'PDF (A4)')}</span>
                        <span className="text-[10px] text-white/50 font-mono">210 × 297 mm</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {!title && (
                <p className="text-[10px] text-amber-400/80 text-center mt-3">
                  {t('⚠ العنوان مطلوب', '⚠ Title required')}
                </p>
              )}
              {title && !savedId && (
                <p className="text-[10px] text-amber-400/80 text-center mt-3">
                  {t('⚠ اضغط "حفظ" أولاً لتفعيل التحميل', '⚠ Click Save first to enable downloads')}
                </p>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Hidden render targets */}
      {isImageCard && (
        <div style={{ position: 'fixed', top: '-15000px', left: '-15000px', pointerEvents: 'none', opacity: 0 }} aria-hidden="true">
          <ImageCardPreview
            ref={cardRef}
            type={type}
            data={data}
            fields={fields}
            exportIsAr={exportIsAr}
            sizeKey={exportSize}
          />
        </div>
      )}
      {!isImageCard && (
        <div style={{ position: 'fixed', top: '-15000px', left: '-15000px', pointerEvents: 'none', opacity: 0 }} aria-hidden="true">
          {/* iter-204 — one BrochurePage per logical PDF page */}
          {brochurePages.map((pg, i) => (
            <BrochurePage
              key={`bp-${i}`}
              ref={(el) => { brochurePagesRef.current[i] = el; }}
              type={type}
              title={title}
              data={data}
              meta={meta}
              exportIsAr={exportIsAr}
              pageSlice={pg}
              pageIndex={i}
              totalPages={brochurePages.length}
            />
          ))}
          <CarRealEstateCardPreview
            ref={cardRef}
            type={type}
            title={title}
            data={data}
            fields={fields}
            images={images}
            meta={meta}
            exportIsAr={exportIsAr}
            sizeKey={exportSize}
          />
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Dynamic field renderer
// ─────────────────────────────────────────────────────────────
function DynamicField({ field, value, onChange, isAr, allData, type }) {
  const label = isAr ? field.label_ar : field.label_en;
  const placeholder = isAr ? field.placeholder_ar : field.placeholder_en;
  const help = isAr ? field.help_ar : field.help_en;

  const wrap = (children) => (
    <div className={field.type === 'textarea' || field.type === 'multi_select' ? 'sm:col-span-2' : ''}>
      <label className="block text-white/70 text-xs mb-1">
        {label} {field.required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {help && <p className="text-[10px] text-white/40 mt-1">{help}</p>}
    </div>
  );

  // iter-198 — plate code: dropdown driven by emirate+category
  if (type === 'plate' && field.key === 'code') {
    const emirate = allData.emirate;
    const category = allData.category;
    const codes = optionsForCode(emirate, category);
    if (!emirate || !category) {
      return wrap(
        <div className="rounded-md bg-[#050505] border border-white/10 text-white/40 text-xs px-3 py-2.5">
          {isAr ? 'اختر الإمارة والفئة أولاً' : 'Pick emirate & category first'}
        </div>,
      );
    }
    if (codes.length === 0) {
      return wrap(
        <div className="rounded-md bg-[#050505] border border-white/10 text-white/50 text-xs px-3 py-2.5">
          {isAr ? 'لا يوجد كود لهذه الفئة' : 'No code for this category'}
        </div>,
      );
    }
    return wrap(
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-md bg-[#050505] border border-white/10 text-white px-3 text-sm"
        data-testid="plate-code-select"
      >
        <option value="">—</option>
        {codes.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>,
    );
  }

  // iter-198 — plate number: numeric only, max 5 chars
  if (type === 'plate' && field.key === 'number') {
    return wrap(
      <Input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value || ''}
        onChange={(e) => {
          const cleaned = e.target.value.replace(/[^0-9]/g, '').slice(0, 5);
          onChange(cleaned);
        }}
        placeholder="30026"
        maxLength={5}
        className="bg-[#050505] border-white/10 text-white text-sm font-mono"
        dir="ltr"
        data-testid="plate-number-input"
      />,
    );
  }

  switch (field.type) {
    case 'textarea':
      return wrap(
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder={placeholder}
          className="w-full rounded-md bg-[#050505] border border-white/10 text-white p-2.5 text-sm"
        />,
      );

    case 'select':
      return wrap(
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-10 rounded-md bg-[#050505] border border-white/10 text-white px-3 text-sm"
        >
          <option value="">—</option>
          {(field.options || []).map(opt => (
            <option key={opt.value} value={opt.value}>{isAr ? opt.label_ar : opt.label_en}</option>
          ))}
        </select>,
      );

    case 'multi_select': {
      const current = Array.isArray(value) ? value : [];
      const toggle = (v) => {
        const next = current.includes(v) ? current.filter(x => x !== v) : [...current, v];
        onChange(next);
      };
      return wrap(
        <div className="flex flex-wrap gap-1.5">
          {(field.options || []).map(opt => {
            const active = current.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium border transition ${
                  active
                    ? 'bg-[#d4af37]/20 border-[#d4af37]/50 text-[#d4af37]'
                    : 'bg-white/[0.02] border-white/10 text-white/60 hover:bg-white/5'
                }`}
              >
                {isAr ? opt.label_ar : opt.label_en}
              </button>
            );
          })}
        </div>,
      );
    }

    case 'boolean':
      return wrap(
        <button
          type="button"
          onClick={() => onChange(!value)}
          className={`w-full h-10 rounded-md border text-sm font-medium transition ${
            value
              ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
              : 'bg-white/[0.02] border-white/10 text-white/50 hover:bg-white/5'
          }`}
        >
          {value ? (isAr ? '✓ نعم' : '✓ Yes') : (isAr ? '✗ لا' : '✗ No')}
        </button>,
      );

    case 'number':
      return wrap(
        <Input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          min={field.min}
          max={field.max}
          placeholder={placeholder}
          className="bg-[#050505] border-white/10 text-white text-sm"
        />,
      );

    case 'price':
      return wrap(
        <div className="relative">
          <Input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder={placeholder}
            className="bg-[#050505] border-white/10 text-white text-sm pe-14"
          />
          <span className="absolute top-1/2 -translate-y-1/2 end-3">
            {/* iter-217c-v11 — dirham icon in input suffix (dark bg → white variant) */}
            <img src="/currency/dirham-white.png" alt="AED" className="h-3.5 w-3.5 object-contain opacity-80" />
          </span>
        </div>,
      );

    case 'phone':
    case 'email':
    case 'url':
    case 'coordinate':
      return wrap(
        <Input
          type={field.type === 'coordinate' ? 'number' : 'text'}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="bg-[#050505] border-white/10 text-white text-sm"
          dir="ltr"
        />,
      );

    default:
      return wrap(
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="bg-[#050505] border-white/10 text-white text-sm"
        />,
      );
  }
}


// ─────────────────────────────────────────────────────────────
// Card preview — iter-198 redesign (Manual PDF look, off-white)
// Renders BOTH story (1080x1920) and post (1080x1080) — the
// parent picks which one by setting `sizeKey`.
// Used for Phone / Plate exports.
// ─────────────────────────────────────────────────────────────
const ImageCardPreview = forwardRef(function ImageCardPreview(
  { type, data, fields, exportIsAr, sizeKey = 'post' }, ref,
) {
  const size = EXPORT_SIZES[sizeKey] || EXPORT_SIZES.post;
  const isStory = sizeKey === 'story';

  const opLabel = (key) => optionLabel(fields.find(f => f.key === key), data[key], exportIsAr);
  const description = exportIsAr
    ? (data.description_ar || data.description || '')
    : (data.description_en || data.description || '');

  return (
    <div
      ref={ref}
      dir={exportIsAr ? 'rtl' : 'ltr'}
      style={{
        width: size.w,
        height: size.h,
        background: BRAND.tint,
        color: BRAND.ink,
        fontFamily: exportIsAr
          ? '"Cairo", "Tajawal", "Segoe UI", Tahoma, Arial, sans-serif'
          : '"Segoe UI", "Roboto", Tahoma, Arial, sans-serif',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Top brand strip — blue↔orange gradient (matches Manual PDF header) */}
      <div style={{ height: isStory ? 14 : 10, background: BRAND.gradient, flexShrink: 0 }} />

      {/* Logo bar — plain, no invented text */}
      <div style={{
        padding: isStory ? '48px 60px 24px' : '32px 44px 18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <img
          src={LOGO_URL}
          alt="Cloud GM FZ-LLC"
          crossOrigin="anonymous"
          style={{
            height: isStory ? 200 : 160,
            width: 'auto',
            objectFit: 'contain',
          }}
        />
      </div>

      {/* Body */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isStory ? 42 : 28,
        padding: isStory ? '20px 60px 40px' : '12px 40px 24px',
        textAlign: 'center',
      }}>
        {type === 'phone' && (
          <PhoneCardBody data={data} fields={fields} exportIsAr={exportIsAr} isStory={isStory} />
        )}
        {type === 'plate' && (
          <PlateCardBody data={data} fields={fields} exportIsAr={exportIsAr} isStory={isStory} />
        )}

        {description && (
          <div style={{
            marginTop: isStory ? 16 : 10,
            fontSize: isStory ? 30 : 22,
            color: BRAND.ink600,
            lineHeight: 1.55,
            maxWidth: isStory ? 900 : 800,
            whiteSpace: 'pre-wrap',
          }}>
            {description}
          </div>
        )}
      </div>

      {/* Footer — matches Manual PDF footer (gradient bar + 5-line contact) */}
      <FooterBlock isStory={isStory} exportIsAr={exportIsAr} />
    </div>
  );
});

function PhoneCardBody({ data, fields, exportIsAr, isStory }) {
  const operator = data.operator || '';
  const prefix = data.prefix || '';
  const number = data.number || '';
  const cat = data.category || '';
  const catColors = { VIP: BRAND.gold, Golden: '#F5C518', Silver: '#94a3b8', Normal: BRAND.blue };
  const catColor = catColors[cat] || BRAND.gold;

  const opField = fields.find((f) => f.key === 'operator');
  const opOption = opField?.options?.find((o) => String(o.value) === String(operator));
  const opLogo = opOption?.icon_url;
  const opBrandColor = opOption?.brand_color;

  return (
    <>
      {cat && (
        <div style={{
          fontSize: isStory ? 30 : 22,
          fontWeight: 900,
          letterSpacing: 5,
          padding: isStory ? '12px 34px' : '9px 24px',
          borderRadius: 999,
          background: `${catColor}20`,
          border: `3px solid ${catColor}`,
          color: catColor,
        }}>
          {cat}
        </div>
      )}
      {operator && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: isStory ? '12px 24px' : '10px 20px',
          borderRadius: 999,
          background: opBrandColor ? `${opBrandColor}15` : 'rgba(59,158,220,0.08)',
          border: `2px solid ${opBrandColor || BRAND.blue}55`,
        }}>
          {opLogo && (
            <img src={opLogo} alt={operator} crossOrigin="anonymous"
                 style={{ height: isStory ? 60 : 46, width: 'auto', objectFit: 'contain' }} />
          )}
          <span style={{ fontSize: isStory ? 32 : 24, color: BRAND.ink, fontWeight: 700 }}>
            {exportIsAr ? (opOption?.label_ar || operator) : (opOption?.label_en || operator)}
          </span>
        </div>
      )}
      <div style={{
        fontSize: isStory ? 140 : 96,
        fontWeight: 900,
        letterSpacing: 4,
        color: BRAND.ink,
        fontFamily: 'monospace',
        direction: 'ltr',
        lineHeight: 1,
      }}>
        {prefix} {number}
      </div>
      {data.price != null && data.price !== '' && (
        <PricePill amount={data.price} note={data.negotiable} isStory={isStory} exportIsAr={exportIsAr} />
      )}
    </>
  );
}

function PlateCardBody({ data, fields, exportIsAr, isStory }) {
  const opLabel = (key) => optionLabel(fields.find(f => f.key === key), data[key], exportIsAr);
  const emirate = data.emirate;
  const cat = data.category || 'private';
  const hasDesign = emirate && hasPlateDesign(emirate, cat);

  return (
    <>
      {hasDesign ? (
        <div style={{
          // iter-217c-v11 — reduced scale from 2.1→1.75 (story) and 1.55→1.4 (post)
          // so the plate never touches the story canvas edges. Combined with
          // width=480 (was 520), horizontal padding remains ~90 px on 1080 story.
          transform: `scale(${isStory ? 1.75 : 1.4})`,
          margin: isStory ? '90px 0 50px' : '48px 0 20px',
        }}>
          <UAEPlate emirate={emirate} category={cat} code={data.code} number={data.number} width={480} />
        </div>
      ) : (
        <div style={{
          fontSize: isStory ? 130 : 96, fontWeight: 900, color: BRAND.ink,
          direction: 'ltr', letterSpacing: 4,
        }}>
          {data.code} {data.number}
        </div>
      )}
      <div style={{
        fontSize: isStory ? 26 : 20,
        color: BRAND.ink400,
        fontWeight: 700,
      }}>
        {opLabel('emirate')} · {opLabel('category')}
      </div>
      {data.price != null && data.price !== '' && (
        <PricePill amount={data.price} isStory={isStory} exportIsAr={exportIsAr} />
      )}
    </>
  );
}

function PricePill({ amount, note, isStory, exportIsAr }) {
  return (
    <div style={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 4,
      padding: isStory ? '18px 40px' : '14px 30px',
      borderRadius: 20,
      background: `linear-gradient(180deg, #fff 0%, #fff7ec 100%)`,
      border: `2px solid ${BRAND.orange}`,
    }}>
      <div style={{ fontSize: isStory ? 20 : 15, color: BRAND.ink400, fontWeight: 700, letterSpacing: 2 }}>
        {exportIsAr ? 'السعر' : 'PRICE'}
      </div>
      <div style={{ fontSize: isStory ? 72 : 56, fontWeight: 900, color: BRAND.gold, direction: 'ltr', lineHeight: 1, display: 'inline-flex', alignItems: 'center', gap: isStory ? 12 : 8 }}>
        {/* iter-217c-v11 — dirham icon replaces "AED" text.
            Icon is on the LEFT of the number in both AR & EN (per user directive).
            Uses black variant because PricePill sits on a WHITE gradient bg. */}
        <img
          src="/currency/dirham-black.png"
          alt="AED"
          crossOrigin="anonymous"
          style={{ height: isStory ? 60 : 46, width: 'auto', objectFit: 'contain' }}
        />
        <span>{amount}</span>
      </div>
      {note && (
        <div style={{ fontSize: isStory ? 18 : 14, color: '#059669', marginTop: 4 }}>
          {exportIsAr ? '✓ قابل للتفاوض' : '✓ Negotiable'}
        </div>
      )}
    </div>
  );
}

function FooterBlock({ isStory, exportIsAr }) {
  return (
    <div style={{
      padding: isStory ? '24px 60px 44px' : '16px 44px 30px',
      flexShrink: 0,
    }}>
      <div style={{ height: 4, background: BRAND.gradient, borderRadius: 3, marginBottom: isStory ? 20 : 12 }} />
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: isStory ? 30 : 18,
        flexWrap: 'wrap',
        fontSize: isStory ? 20 : 14,
        fontWeight: 700,
      }}>
        <span style={{ color: BRAND.blue700 }}>🌐 {CONTACT.sites.join(' · ')}</span>
        <span style={{ color: BRAND.orange700, direction: 'ltr' }}>📞 {CONTACT.phone}</span>
      </div>
      <div style={{
        marginTop: isStory ? 10 : 6,
        fontSize: isStory ? 17 : 12,
        color: BRAND.ink600,
        textAlign: 'center',
      }}>
        ✉ {CONTACT.emails.join(' · ')}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Car / Real Estate — social image (Story/Post) reused shell
// ─────────────────────────────────────────────────────────────
const CarRealEstateCardPreview = forwardRef(function CarRealEstateCardPreview(
  { type, title, data, fields, images, meta, exportIsAr, sizeKey = 'post' }, ref,
) {
  const size = EXPORT_SIZES[sizeKey] || EXPORT_SIZES.post;
  const isStory = sizeKey === 'story';
  const opLabel = (key) => optionLabel(fields.find(f => f.key === key), data[key], exportIsAr);

  const description = exportIsAr
    ? (data.description_ar || data.description || '')
    : (data.description_en || data.description || '');

  // Top 4 spec chips (excluding title/description/notes/price)
  const specChips = fields
    .filter((f) => !['title_ar', 'title_en', 'description_ar', 'description_en', 'description', 'internal_notes', 'price'].includes(f.key))
    .filter((f) => f.type !== 'textarea')
    .map((f) => {
      const v = data[f.key];
      if (v === undefined || v === null || v === '') return null;
      const val = Array.isArray(v)
        ? v.map((x) => optionLabel(f, x, exportIsAr)).join('، ')
        : (typeof v === 'boolean' ? (exportIsAr ? 'نعم' : 'Yes') : optionLabel(f, v, exportIsAr));
      return { label: (exportIsAr ? f.label_ar : f.label_en), value: String(val) };
    })
    .filter(Boolean)
    .slice(0, isStory ? 6 : 4);

  return (
    <div
      ref={ref}
      dir={exportIsAr ? 'rtl' : 'ltr'}
      style={{
        width: size.w, height: size.h,
        background: BRAND.tint, color: BRAND.ink,
        fontFamily: exportIsAr
          ? '"Cairo", "Tajawal", "Segoe UI", Tahoma, Arial, sans-serif'
          : '"Segoe UI", "Roboto", Tahoma, Arial, sans-serif',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Top strip */}
      <div style={{ height: isStory ? 14 : 10, background: BRAND.gradient, flexShrink: 0 }} />

      {/* Logo */}
      <div style={{ padding: isStory ? '40px 60px 20px' : '26px 40px 12px', display: 'flex', justifyContent: 'center' }}>
        <img
          src={LOGO_URL}
          alt="Cloud GM FZ-LLC"
          crossOrigin="anonymous"
          style={{ height: isStory ? 160 : 130, width: 'auto', objectFit: 'contain' }}
        />
      </div>

      {/* Hero image */}
      {images[0] && (
        <div style={{
          margin: isStory ? '10px 60px' : '6px 44px',
          height: isStory ? 720 : 500,
          borderRadius: 22,
          overflow: 'hidden',
          border: `4px solid ${BRAND.blue}22`,
          boxShadow: '0 10px 30px rgba(0,0,0,0.10)',
        }}>
          <img src={images[0]} alt="hero" style={{ width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
        </div>
      )}

      {/* Spec chips */}
      {specChips.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: isStory ? 14 : 10,
          padding: isStory ? '0 60px 8px' : '0 44px 4px',
        }}>
          {specChips.map((c, i) => (
            <div key={i} style={{
              padding: isStory ? '10px 18px' : '8px 14px',
              borderRadius: 999,
              background: '#fff',
              border: `1.5px solid ${BRAND.ink200}`,
              fontSize: isStory ? 20 : 14,
              color: BRAND.ink,
            }}>
              <span style={{ color: BRAND.ink400, fontWeight: 700, marginInlineEnd: 6 }}>{c.label}:</span>
              <span style={{ fontWeight: 700 }}>{c.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Description */}
      {description && (
        <div style={{
          padding: isStory ? '10px 80px' : '4px 60px',
          fontSize: isStory ? 26 : 18,
          color: BRAND.ink600,
          lineHeight: 1.5,
          textAlign: 'center',
          whiteSpace: 'pre-wrap',
          flexShrink: 0,
        }}>
          {description}
        </div>
      )}

      {/* Price */}
      {data.price != null && data.price !== '' && (
        <div style={{
          padding: isStory ? '20px 60px 8px' : '10px 44px 4px',
          display: 'flex',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <PricePill amount={data.price} isStory={isStory} exportIsAr={exportIsAr} />
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: 'auto' }}>
        <FooterBlock isStory={isStory} exportIsAr={exportIsAr} />
      </div>
    </div>
  );
});


// ─────────────────────────────────────────────────────────────
// iter-204 — Build spec rows array from raw data
// ─────────────────────────────────────────────────────────────
function buildSpecRows(data, fields, exportIsAr) {
  return fields
    .filter((f) => {
      if (f.type === 'textarea') return false;
      if (['title_ar', 'title_en', 'description_ar', 'description_en', 'description', 'internal_notes'].includes(f.key)) return false;
      const v = data[f.key];
      return v !== undefined && v !== null && v !== '';
    })
    .map((f) => {
      const label = exportIsAr ? f.label_ar : f.label_en;
      const raw = data[f.key];
      let display;
      if (Array.isArray(raw)) {
        display = raw.map((v) => optionLabel(f, v, exportIsAr)).join('، ');
      } else if (typeof raw === 'boolean') {
        display = raw ? (exportIsAr ? 'نعم' : 'Yes') : (exportIsAr ? 'لا' : 'No');
      } else if (f.key === 'price') {
        display = `$<DirhamPrice value={raw} decimals={2} size="sm" />`;
      } else {
        display = optionLabel(f, raw, exportIsAr);
      }
      const strValue = String(display);
      // iter-204: A field is "wide" if its value is long enough to need the full row.
      // Short values (numbers, dates, single-word enums) → 2-col; long values → 1-col.
      const isWide = strValue.length > 45;
      return { label, value: strValue, isWide };
    });
}

// ─────────────────────────────────────────────────────────────
// iter-204 — Group spec rows into 2-column pairs (short) or full-width (long)
// Returns an array of "spec blocks" where each block is either
// { type: 'pair', a, b } or { type: 'full', row }
// ─────────────────────────────────────────────────────────────
function packSpecBlocks(specRows) {
  const blocks = [];
  const shorts = [];
  const longs = [];
  specRows.forEach((r) => (r.isWide ? longs.push(r) : shorts.push(r)));

  // Pair up the short ones
  for (let i = 0; i < shorts.length; i += 2) {
    blocks.push({
      type: 'pair',
      a: shorts[i],
      b: shorts[i + 1] || null,
    });
  }
  // Append long ones as full-width
  longs.forEach((r) => blocks.push({ type: 'full', row: r }));
  return blocks;
}

// ─────────────────────────────────────────────────────────────
// iter-204 — Split content across A4 pages without slicing sections.
// Height budget (px @ 96dpi ~ A4):
//   Page 1 usable body:  ~700px (after header+title+footer)
//   Later pages usable:  ~950px (only header logo band + footer)
// Approx heights per block:
//   hero image:  340px
//   spec pair:    36px
//   spec full:    38px
//   description:  110px (min) + ~14px per 60 chars
//   gallery:     200px (2×2 grid up to 4 images)
// ─────────────────────────────────────────────────────────────
function computeBrochurePages({ data, fields, images, exportIsAr }) {
  const specRows = buildSpecRows(data, fields, exportIsAr);
  const specBlocks = packSpecBlocks(specRows);
  const description = exportIsAr
    ? (data.description_ar || data.description || '')
    : (data.description_en || data.description || '');
  const hasDescription = description && description.trim().length > 0;
  const galleryImages = images.slice(1, 5);
  const hasGallery = galleryImages.length > 0;

  const heightOf = (block) => (block.type === 'pair' ? 40 : 42);
  const descHeight = () => 100 + Math.ceil((description || '').length / 60) * 16;

  const pages = [];
  const FIRST_PAGE_BUDGET = 640;   // after logo, title, hero
  const OTHER_PAGE_BUDGET = 900;   // after logo + footer

  // Page 1 always starts with the hero + title. Push spec blocks that fit
  // (leave some cushion at the bottom of ~40px).
  let currentPage = {
    showHero: true,
    showTitle: true,
    hero: images[0] || null,
    specs: [],
    description: null,
    gallery: null,
    isFirst: true,
  };
  let used = 0;
  let budget = FIRST_PAGE_BUDGET;

  // Try to fit description on page 1 first if it's small AND we have room
  // after packing at least a few specs. Otherwise it goes later.

  const commitPage = () => {
    pages.push(currentPage);
    currentPage = { showHero: false, showTitle: false, hero: null, specs: [], description: null, gallery: null, isFirst: false };
    used = 0;
    budget = OTHER_PAGE_BUDGET;
  };

  // Pack spec blocks first
  for (let i = 0; i < specBlocks.length; i += 1) {
    const b = specBlocks[i];
    const h = heightOf(b);
    if (used + h > budget - 40) {
      commitPage();
    }
    currentPage.specs.push(b);
    used += h;
  }

  // Fit description
  if (hasDescription) {
    const dh = descHeight();
    if (used + dh > budget - 40) commitPage();
    currentPage.description = description;
    used += dh;
  }

  // iter-208 — Gallery ALWAYS on its own dedicated page (larger images).
  if (hasGallery) {
    if (used > 0) commitPage();               // commit any accumulated content first
    currentPage.gallery = galleryImages;
    currentPage.galleryLarge = true;          // signal to BrochurePage: render bigger
    used = 700;                               // enough to force commit later
  }

  // Always commit at least page 1 if empty
  if (used > 0 || pages.length === 0) commitPage();

  return pages;
}

// ─────────────────────────────────────────────────────────────
// PDF Brochure Page (Car / Real Estate) — iter-204 multi-page.
// Fixed A4 size (794×1123 @ 96dpi). Every page carries the logo header
// + footer. Only page 1 shows the title + hero. No content slicing.
// ─────────────────────────────────────────────────────────────
const BrochurePage = forwardRef(function BrochurePage(
  { type, title, data, meta, exportIsAr, pageSlice, pageIndex, totalPages }, ref,
) {
  const dir = exportIsAr ? 'rtl' : 'ltr';
  const align = exportIsAr ? 'right' : 'left';

  const displayTitle = exportIsAr ? (data.title_ar || title) : (data.title_en || title);
  const typeLabel = exportIsAr ? meta.ar : meta.en;
  const hero = pageSlice.hero || null;

  const sectionHdrStyle = {
    fontSize: 13, fontWeight: 800,
    color: BRAND.blue700,
    letterSpacing: exportIsAr ? 0 : 2,
    textTransform: exportIsAr ? 'none' : 'uppercase',
    marginBottom: 8, paddingBottom: 5,
    borderBottom: `2px solid ${BRAND.blue}`,
    textAlign: align,
  };

  return (
    <div
      ref={ref}
      dir={dir}
      style={{
        width: 794, height: 1123,               // exact A4 @ 96 dpi
        background: BRAND.tint,
        color: BRAND.ink,
        fontFamily: exportIsAr
          ? '"Cairo", "Tajawal", "Segoe UI", Tahoma, Arial, sans-serif'
          : '"Segoe UI", "Roboto", Tahoma, Arial, sans-serif',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header — compact on continuation pages */}
      <div style={{
        margin: pageSlice.isFirst ? 18 : 12,
        borderRadius: 12,
        overflow: 'hidden',
        border: `1px solid ${BRAND.ink200}`,
        background: '#ffffff',
        flexShrink: 0,
      }}>
        <div style={{ height: 5, background: BRAND.gradient }} />
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 16, padding: pageSlice.isFirst ? '14px 22px' : '8px 22px',
        }}>
          <img
            src={LOGO_URL}
            alt="Cloud GM"
            crossOrigin="anonymous"
            style={{
              height: pageSlice.isFirst ? 88 : 44,
              width: 'auto',
              objectFit: 'contain',
            }}
          />
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '4px 28px 12px', flex: 1, overflow: 'hidden' }}>
        {/* Type + Title (page 1 only) */}
        {pageSlice.showTitle && (
          <div style={{ textAlign: 'center', marginBottom: 14 }}>
            <div style={{
              fontSize: 11, color: BRAND.ink400,
              letterSpacing: exportIsAr ? 0 : 4,
              textTransform: exportIsAr ? 'none' : 'uppercase',
              marginBottom: 5, fontWeight: 700,
            }}>
              {typeLabel}
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: BRAND.ink, lineHeight: 1.2 }}>
              {displayTitle}
            </div>
          </div>
        )}

        {/* Hero (page 1 only) */}
        {hero && (
          <div style={{
            width: '100%', height: 280, borderRadius: 12, overflow: 'hidden',
            marginBottom: 14, border: `1px solid ${BRAND.ink200}`,
            background: '#fff', flexShrink: 0,
          }}>
            <img src={hero} alt="hero" style={{ width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
          </div>
        )}

        {/* Specs — 2 columns for shorts, full width for longs */}
        {pageSlice.specs.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={sectionHdrStyle}>{exportIsAr ? 'المواصفات' : 'Specifications'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {pageSlice.specs.map((block, idx) => {
                if (block.type === 'pair') {
                  return (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <SpecCell row={block.a} align={align} />
                      {block.b ? <SpecCell row={block.b} align={align} /> : <div />}
                    </div>
                  );
                }
                return (
                  <SpecCell key={idx} row={block.row} align={align} full />
                );
              })}
            </div>
          </div>
        )}

        {/* Description */}
        {pageSlice.description && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ ...sectionHdrStyle, marginBottom: 6 }}>
              {exportIsAr ? 'الوصف' : 'Description'}
            </div>
            <div style={{
              fontSize: 12, lineHeight: 1.65, color: BRAND.ink600,
              textAlign: align, whiteSpace: 'pre-wrap', background: '#fff',
              padding: '10px 12px', borderRadius: 10, border: `1px solid ${BRAND.ink200}`,
            }}>
              {pageSlice.description}
            </div>
          </div>
        )}

        {/* Gallery — iter-208: when on its own dedicated page, render LARGE images */}
        {pageSlice.gallery && pageSlice.gallery.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <div style={sectionHdrStyle}>{exportIsAr ? 'صور إضافية' : 'Gallery'}</div>
            {pageSlice.galleryLarge ? (
              // Dedicated gallery page — big images, up to 2×2 grid (~400px each).
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
                {pageSlice.gallery.map((img, i) => (
                  <div key={i} style={{
                    height: pageSlice.gallery.length <= 2 ? 800 : 400,
                    borderRadius: 12, overflow: 'hidden',
                    border: `1px solid ${BRAND.ink200}`,
                    gridColumn: pageSlice.gallery.length === 1 ? 'span 2' : undefined,
                  }}>
                    <img src={img} alt={`i-${i}`}
                         style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                         crossOrigin="anonymous" />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {pageSlice.gallery.map((img, i) => (
                  <div key={i} style={{ height: 160, borderRadius: 10, overflow: 'hidden',
                                        border: `1px solid ${BRAND.ink200}` }}>
                    <img src={img} alt={`i-${i}`}
                         style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                         crossOrigin="anonymous" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer — always at the very bottom */}
      <div style={{ padding: '8px 28px 14px', flexShrink: 0 }}>
        <div style={{ height: 4, background: BRAND.gradient, borderRadius: 3, marginBottom: 8 }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 14, fontSize: 11, fontWeight: 700 }}>
          <span style={{ color: BRAND.blue700 }}>🌐 {CONTACT.sites.join(' · ')}</span>
          <span style={{ color: BRAND.orange700, direction: 'ltr' }}>📞 {CONTACT.phone}</span>
        </div>
        <div style={{ marginTop: 3, fontSize: 10, color: BRAND.ink600, textAlign: 'center' }}>
          ✉ {CONTACT.emails.join(' · ')}
        </div>
        <div style={{ marginTop: 4, fontSize: 9, color: BRAND.ink400, textAlign: 'center' }}>
          © {new Date().getFullYear()} Cloud GM FZ-LLC — {exportIsAr ? 'كل الحقوق محفوظة' : 'All rights reserved'}
          {totalPages > 1 && (
            <> · {exportIsAr ? `صفحة ${pageIndex + 1} من ${totalPages}` : `Page ${pageIndex + 1} of ${totalPages}`}</>
          )}
        </div>
      </div>
    </div>
  );
});

function SpecCell({ row, align, full = false }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 10,
        padding: '8px 12px',
        background: '#fff',
        border: `1px solid ${BRAND.ink200}`,
        borderRadius: 8,
        fontSize: 12,
        minHeight: full ? 36 : 34,
      }}
    >
      <span
        style={{
          fontWeight: 700, color: BRAND.ink600,
          textAlign: align, flexShrink: 0, maxWidth: full ? '30%' : '45%',
        }}
      >
        {row.label}
      </span>
      <span
        style={{
          color: BRAND.ink, textAlign: align === 'right' ? 'left' : 'right',
          fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: full ? 'normal' : 'nowrap',
        }}
      >
        {row.value}
      </span>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Image uploader
// ─────────────────────────────────────────────────────────────
function ImageUploader({ images, setImages, handleImages, t }) {
  return (
    <div>
      <label className="block text-white/70 text-sm mb-2">
        {t('الصور', 'Images')} ({images.length})
      </label>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {images.map((img, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-white/5 border border-white/10">
            <img src={img} alt={`img-${i}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => setImages(imgs => imgs.filter((_, ix) => ix !== i))}
              className="absolute top-1 end-1 p-1 rounded-md bg-red-500/80 hover:bg-red-500 text-white"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <label className="aspect-square rounded-lg bg-white/5 border-2 border-dashed border-white/20 hover:border-[#d4af37]/50 flex flex-col items-center justify-center cursor-pointer transition">
          <Upload className="h-6 w-6 text-white/40 mb-1" />
          <span className="text-[10px] text-white/50">{t('رفع', 'Upload')}</span>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleImages(e.target.files)}
            className="hidden"
            data-testid="company-marketing-images-input"
          />
        </label>
      </div>
      <p className="text-[10px] text-white/40 mt-2">
        {t('أول صورة تُستخدم كغلاف الصورة/PDF.', 'First image is used as the card/PDF hero.')}
      </p>
    </div>
  );
}
